/**
 * 순수 이동 수식. 서버(`World`)와 클라이언트 예측이 **같은 코드**를 쓰기 위해 분리했다
 * (docs/02-tech-spec.md §2.1 — shared/sim은 Phaser/DOM/Node에 의존하지 않는다).
 */

/**
 * px/sec. 2026-08에 전원(플레이어·몬스터·티모시)을 **원래의 75%**로 낮췄다 — 맵을
 * 가로지르는 시간이 너무 짧아 낮의 채집 동선과 밤의 방어 배치가 둘 다 의미를 잃었다.
 * 한때 절반(50)까지 내렸는데 조작이 답답해서 되돌린 값이다.
 * 보스 돌진(attacks[].dash.speed)은 이동이 아니라 공격이라 그대로 뒀다.
 */
export const PLAYER_SPEED = 75;

/**
 * 입력 벡터를 -1~1로 clamp하고, 대각선 정규화까지 적용한다.
 * clamp만 하면 (1,1) 같은 대각선 입력이 그대로 통과해 속도가 √2배(약 141%)가 된다.
 */
export function normalizeMoveVector(moveX: number, moveY: number): { moveX: number; moveY: number } {
  let x = Math.min(1, Math.max(-1, moveX));
  let y = Math.min(1, Math.max(-1, moveY));

  const length = Math.hypot(x, y);
  if (length > 1) {
    x /= length;
    y /= length;
  }

  return { moveX: x, moveY: y };
}

/**
 * 정규화된 입력 벡터로 한 스텝(dtSeconds) 이동시킨 결과 좌표를 반환한다.
 * speedMultiplier: 음식 스태미나·아드레날린 배율. 클라이언트 예측도 서버가 동기화해 준
 * 같은 배율을 넘겨야 한다 — 다르면 버프 지속 중 계속 되감긴다(rubber-banding).
 */
export function stepPosition(
  x: number,
  y: number,
  moveX: number,
  moveY: number,
  dtSeconds: number,
  speedMultiplier = 1,
): { x: number; y: number } {
  return {
    x: x + moveX * PLAYER_SPEED * speedMultiplier * dtSeconds,
    y: y + moveY * PLAYER_SPEED * speedMultiplier * dtSeconds,
  };
}

/**
 * 플레이어를 장애물과 겹치지 않는 선에서 이동시킨다(축 슬라이딩).
 *
 * `World.movePlayer`와 클라이언트 예측(`PlayerPredictor`)이 **같은 함수**를 쓴다 —
 * 그래서 충돌 판정을 인자로 받는다(`isBlocked`). 서버는 `World.isBlockedForPlayer`를,
 * 클라이언트는 최근 스냅샷으로 만든 동등한 함수를 넘긴다. 둘이 같은 판정 규칙
 * (`isPlayerBlocked`, playerCollision.ts)을 쓰는 한 예측이 서버와 어긋나지 않는다.
 *
 * 전체 이동이 막히면 X축만, 그것도 막히면 Y축만 시도한다 — 벽에 대각선으로 부딪혔을 때
 * 완전히 멈추는 대신 벽을 따라 미끄러지듯 이동하게 하기 위함이다.
 */
export function resolvePlayerMove(
  x: number,
  y: number,
  moveX: number,
  moveY: number,
  dtSeconds: number,
  speedMultiplier: number,
  isBlocked: (x: number, y: number) => boolean,
): { x: number; y: number } {
  const full = stepPosition(x, y, moveX, moveY, dtSeconds, speedMultiplier);
  if (!isBlocked(full.x, full.y)) return full;

  const xOnly = stepPosition(x, y, moveX, 0, dtSeconds, speedMultiplier);
  if (!isBlocked(xOnly.x, xOnly.y)) return xOnly;

  const yOnly = stepPosition(x, y, 0, moveY, dtSeconds, speedMultiplier);
  if (!isBlocked(yOnly.x, yOnly.y)) return yOnly;

  return { x, y };
}
